// AppMenuBar — the app's ONE menubar.
//
// ── What this replaces ───────────────────────────────────────────────────────
// There used to be two overlapping menus:
//
//   1. AppMenu.jsx — a floating "Menu ▾" pinned to the top-left, mounted in every
//      one of App's render branches. File / Switch Character / All characters /
//      Conversation / Appearance / Texture Quality / Load Preset / Reset
//      Blueprints / Clear Cache.
//   2. BlueprintEditor's internal MenuBar — File / Character / Animation /
//      Assets / Build / View plus the Body·Face·Events·Content tab strip.
//
// They silently disagreed. BOTH had an "Export" (one wrote blueprints.json from
// every aos-blueprint* key, the other wrote blueprint.json from the current
// character) and BOTH had a blueprint reset with different mechanics. Two items
// with one label is a bug, so every command here carries a scope word: "this
// character's blueprint" vs "all characters' blueprints". No two items share a
// name.
//
// ── Why the floating menu existed, and why it's gone ─────────────────────────
// Not because the lists needed merging — because the menubar was OWNED by the
// blueprint editor, so it did not exist until the editor did, and it vanished
// during a character load. That's the window a user got stuck in, and the
// floating overlay was the workaround.
//
// So this menubar depends on NOTHING that can be loading:
//   • It mounts above every App render branch, before any character request fires
//     (see the `shell()` wrapper in App.jsx) and never unmounts across them.
//   • Editor-scoped commands arrive through the menubarCommands registry. Nothing
//     published → the item is still LISTED, disabled, with the reason inline.
//     Never hidden, never a popup, never a silent no-op.
//   • The character chip's dropdown is always openable, including mid-load. That
//     alone is what deleted the left menu.
//
// ── Styling note ─────────────────────────────────────────────────────────────
// Laid out to the approved "Menubar Consolidation" Claude Design export (option
// 1a, with 1c's naming discipline). That export inlines literal color values, and
// every one of them turns out to BE a default-palette token value — its page,
// panel and graph backgrounds, its foreground and two dim-foreground greys, and
// its accent all match --m-bg / --m-panel / --m-bg-graph / --m-fg / --m-fg-2 /
// --m-fg-3 / --m-accent exactly. So this uses the TOKENS: pixel-identical on the
// default palette, and unlike inlined literals it follows a palette switch. The
// few values with no token equivalent (the design's translucent accent wash and
// border, and its 6% white hover) are derived with color-mix from the nearest
// token rather than hardcoded, for the same reason.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import DirtyBadge from './DirtyBadge'
import { PipelineGlyph, MONO, UI_FONT } from './PipelineGlyph.jsx'
import { pill, head, menuSurface } from '../lib/studioStyles.js'
import { dialog } from '../lib/dialog'
import { menubarCommands } from '../lib/menubarCommands.js'
import { deriveChipState } from '../lib/menubarChipState.js'
import {
  collectBlueprintEntries,
  applyBlueprintEntries,
  resetAllBlueprints,
  clearAllLocalStorage,
} from '../lib/blueprintRecovery.js'
import { loadBlueprint } from '../lib/loadBlueprint'
import { animCache } from '../lib/animCache'
import { clearAll as clearSplatCache } from '../lib/splatCache'
import { clearAamFileCache } from '../lib/aamAssetCache'
import { readBlueprintImportFile, importBlueprintEnvelope } from '../lib/blueprintAssetTransfer'
import { clearStaticAssetCache } from '../lib/staticAssetCache'
import {
  TIER_ORDER, getSetting, setSetting, getAutoTier,
  subscribe as subscribeTextureQuality,
} from '../lib/textureQuality'
import { shellLayout } from '../lib/shellLayout.js'
// Shared with the tool rail and the outliner rows, so one surface is marked
// identically wherever it is offered.
import { SURFACE_GLYPH } from '../lib/outlinerTree.js'
import { bakeSettingsStore } from '../lib/bakeSettingsStore'
// Accelerator hints (Wave 2C): every display string comes from the shortcut
// registry via formatKeys — no hardcoded glyphs here, so the menu and the
// help window can never spell the same shortcut two ways.
import { formatKeys, getShortcut } from '../lib/shortcutRegistry.js'
import { activeEnvAssetId } from '../lib/activeEnvironmentAsset'

// Shipped blueprint presets — files in public/blueprints/. Adding one is a code
// change, so a hardcoded list beats a manifest fetch for two entries.
const PRESETS = [
  { label: 'Conversational', url: 'blueprints/conversational.json' },
  { label: 'Isaac', url: 'blueprints/isaac.json' },
]

const ACCENT_WASH = 'color-mix(in srgb, var(--m-accent) 18%, transparent)'
const ACCENT_BORDER = 'color-mix(in srgb, var(--m-accent) 55%, transparent)'
// The design's 6% white hover. Derived from --m-fg so a light palette gets a dark
// wash rather than an invisible white one.
const HOVER_BG = 'color-mix(in srgb, var(--m-fg) 6%, transparent)'
const DANGER_WASH = 'color-mix(in srgb, var(--m-danger) 15%, transparent)'

// Reasons, written once. Each names the missing precondition rather than the
// symptom, because the item stays visible and the text IS the explanation.
const NO_CHARACTER = 'no character loaded'
const NO_EDITOR = 'blueprint editor not open'
const NO_ENVIRONMENT = 'no environment loaded'

// ── Primitives ───────────────────────────────────────────────────────────────

function Divider() {
  return <div style={{ height: 1, background: 'var(--m-line)', margin: '4px 0' }} />
}

function SectionLabel({ children }) {
  return (
    <div style={{
      ...head, padding: '6px 11px 4px', whiteSpace: 'nowrap',
    }}>{children}</div>
  )
}

/**
 * One dropdown row.
 *
 * `disabledReason` is the load-bearing prop: with it set the row renders dim with
 * the reason as a trailing mono note and does nothing on click. A command the app
 * cannot currently perform must still be FINDABLE — hiding it teaches the user it
 * doesn't exist, and a popup after the click teaches them the menu lies.
 */
function Item({ label, onClick, disabledReason, checked, danger, note, hint, testId }) {
  const disabled = !!disabledReason
  const color = disabled ? 'var(--m-fg-3)'
    : danger ? 'var(--m-danger)'
      : checked ? 'var(--m-accent)' : 'var(--m-fg-2)'

  return (
    <div
      data-testid={testId}
      data-disabled={disabled ? '1' : '0'}
      onClick={disabled ? undefined : onClick}
      title={disabledReason || undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '5px 11px', margin: '0 5px',
        borderRadius: 'var(--m-radius-sm)',
        fontSize: 11, fontFamily: UI_FONT, whiteSpace: 'nowrap',
        color,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background var(--m-ease), color var(--m-ease)',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = danger ? DANGER_WASH : HOVER_BG }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >
      {/* A fixed gutter so checked and unchecked rows keep one left edge. */}
      <span style={{ width: 9, flexShrink: 0, color: 'var(--m-accent)', fontSize: 9 }}>
        {checked ? '●' : ''}
      </span>
      <span>{label}</span>
      <span style={{ flex: 1 }} />
      {(disabledReason || note) && (
        <span style={{ fontFamily: UI_FONT, fontSize: 8.5, color: 'var(--m-fg-3)' }}>
          {disabledReason || note}
        </span>
      )}
      {/* Accelerator hint (Wave 2C) — the shortcut's platform display string,
          right-aligned and muted, mirroring the NewAssetMenu / context-menu
          `hint` convention. Produced by shortcutRegistry.formatKeys upstream. */}
      {hint && (
        <span
          data-testid={testId ? `${testId}-hint` : undefined}
          style={{ fontFamily: MONO, fontSize: 9, color: 'var(--m-fg-3)', letterSpacing: '.04em' }}
        >{hint}</span>
      )}
    </div>
  )
}

function Dropdown({ open, children, align = 'left', minWidth = 248, testId }) {
  if (!open) return null
  return (
    <div
      data-testid={testId}
      style={{
        position: 'absolute', top: '100%', [align]: 0, marginTop: 6,
        minWidth,
        background: 'var(--m-panel)',
        border: '1px solid var(--m-line-hi)',
        ...menuSurface, padding: '5px 0',
        animation: 'aos-menu-fade .09s ease-out',
        zIndex: 20,
        maxHeight: 'calc(100vh - 60px)', overflowY: 'auto',
      }}
    >{children}</div>
  )
}

function MenuButton({ label, open, onClick, onMouseEnter, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      style={{
        all: 'unset', boxSizing: 'border-box', cursor: 'pointer',
        fontSize: 12, padding: '5px 12px', ...pill,
        color: open ? 'var(--m-fg)' : 'var(--m-fg-2)',
        background: open ? HOVER_BG : 'transparent',
      }}
    >{label}</button>
  )
}

// ── Character chip ───────────────────────────────────────────────────────────

/**
 * The leftmost control: which character you are editing, and the only way to
 * change it. Absorbs the old floating menu's "Switch Character" + "All
 * characters…" sections.
 *
 * It is interactive in every state, including mid-load — that is the entire
 * reason the floating menu could be deleted. It never switches characters
 * itself: `onSwitch` is App's handleSwitchCharacter, which routes through the
 * UnsavedChangesModal. Bypassing that would silently drop a dirty draft.
 */
function CharacterChip({
  character, switchingTo, characters, recents, rigStatus, rigStatusById,
  loading, loadingStage, progress, dirtyState,
  open, onToggle, onSwitch, onOpenLauncher, switchReason,
}) {
  const chip = useMemo(() => deriveChipState({
    character, switchingTo, loading, progress, stage: loadingStage, rigStatus, dirtyState,
  }), [character, switchingTo, loading, progress, loadingStage, rigStatus, dirtyState])

  // Recents first (the order the launcher uses), then everything else, with the
  // active character dropped — "switch to the thing you're already on" is not an
  // action. Capped so a 40-character catalog doesn't become a scrolling wall;
  // the full grid is one click away behind "Back to launcher".
  const others = useMemo(() => {
    const byId = new Map((characters ?? []).map(c => [c.id, c]))
    if (character?.id) byId.delete(character.id)
    const ordered = []
    for (const r of recents ?? []) {
      const c = byId.get(r.id)
      if (c) { ordered.push(c); byId.delete(r.id) }
    }
    return [...ordered, ...byId.values()].slice(0, 8)
  }, [characters, recents, character?.id])

  return (
    <div style={{ position: 'relative' }} data-drop="chip">
      <div
        data-testid="character-chip"
        data-character={character?.id ?? ''}
        data-loading={loading ? '1' : '0'}
        onClick={onToggle}
        title={character ? `${chip.name} — ${chip.sub}` : 'No character loaded'}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '4px 12px 4px 6px',
          border: `1px solid ${open ? ACCENT_BORDER : 'var(--m-line-hi)'}`,
          background: open ? ACCENT_WASH : 'transparent',
          borderRadius: 999, cursor: 'pointer',
          transition: 'background var(--m-ease), border-color var(--m-ease)',
        }}
      >
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          background: 'var(--m-bg-graph)', border: '1px solid var(--m-line)',
        }}>
          <PipelineGlyph status={character ? (rigStatus ?? 'unknown') : 'unknown'} size={16} />
        </span>

        {/* The microanimation, and the only one here. `key` is the switch target,
            so React remounts this block the instant a different character is
            picked and the 120ms fade-in plays — the click reads as a TRANSITION
            to something new rather than a label silently mutating. Keyed on
            chip.switchingId alone (null in every settled state), so an ordinary
            load, a byte-count tick or a dirty-state change never re-triggers it:
            an animation that fires on things the user did not do is noise, and
            this one exists to answer "did my click land?".
            The keyframe + its reduced-motion rule live in styles/index.css,
            beside the `spin` keyframe this chip's spinner already uses. */}
        <span
          key={chip.switchingId ?? 'settled'}
          data-chip-switch={chip.switchingId ? '1' : undefined}
          style={{
            display: 'grid', gap: 1, minWidth: 0,
            animation: chip.switchingId ? 'aos-chip-switch .12s ease-out' : undefined,
          }}
        >
          <span
            data-testid="character-chip-name"
            style={{
              fontSize: 11.5, fontWeight: 500, lineHeight: 1.1, whiteSpace: 'nowrap',
              color: chip.muted ? 'var(--m-fg-2)' : 'var(--m-fg)',
            }}
          >{chip.name}</span>
          <code
            data-testid="character-chip-sub"
            style={{
              fontFamily: MONO, fontSize: 8.5, lineHeight: 1.1, whiteSpace: 'nowrap',
              // The load readout is the one sub-line worth the accent: it's
              // changing, and it's the answer to "is this stuck?".
              color: chip.spinning ? 'var(--m-accent)' : 'var(--m-fg-3)',
            }}
          >{chip.sub}</code>
        </span>

        {/* Next to the thing it describes, per the UX proximity rule. */}
        <DirtyBadge state={chip.dirtyState} size={8} />

        {chip.spinning && (
          <span
            aria-hidden="true"
            style={{
              width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
              border: '1.5px solid color-mix(in srgb, var(--m-fg) 15%, transparent)',
              borderTopColor: 'var(--m-accent)',
              animation: 'spin .7s linear infinite',
            }}
          />
        )}
        <span style={{ fontSize: 8, color: 'var(--m-fg-3)' }}>▾</span>
      </div>

      <Dropdown open={open} minWidth={236} testId="character-chip-menu">
        <SectionLabel>Switch to</SectionLabel>
        {others.length === 0 && (
          <div style={{ padding: '5px 11px', fontFamily: UI_FONT, fontSize: 10, color: 'var(--m-fg-3)', fontStyle: 'italic' }}>
            {(characters?.length ?? 0) > 0 ? '(no other characters)' : '(character list still loading)'}
          </div>
        )}
        {others.map(c => (
          <Item
            key={c.id}
            testId={`character-chip-item-${c.id}`}
            label={c.label ?? c.id}
            note={rigStatusById?.[c.id] && rigStatusById[c.id] !== 'unknown' ? rigStatusById[c.id] : undefined}
            disabledReason={switchReason}
            onClick={() => onSwitch?.(c.id)}
          />
        ))}
        <Divider />
        {/* The launcher's only deliberate entry point — the same openLauncher
            callback the old floating menu's "⌂ All characters…" used. ONE item,
            not two: "all characters" and "back to launcher" were the same
            action, and two names for one thing is the bug this consolidation
            exists to remove. */}
        <div
          data-testid="character-chip-launcher"
          onClick={switchReason ? undefined : onOpenLauncher}
          title={switchReason || 'Show every character'}
          style={{
            padding: '5px 11px', margin: '0 5px', borderRadius: 'var(--m-radius-sm)',
            fontSize: 11, fontFamily: UI_FONT, whiteSpace: 'nowrap',
            color: switchReason ? 'var(--m-fg-3)' : 'var(--m-accent)',
            opacity: switchReason ? 0.55 : 1,
            cursor: switchReason ? 'default' : 'pointer',
            transition: 'background var(--m-ease)',
          }}
          onMouseEnter={e => { if (!switchReason) e.currentTarget.style.background = ACCENT_WASH }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
        >↩ Back to launcher{switchReason ? ` — ${switchReason}` : ''}</div>
      </Dropdown>
    </div>
  )
}

// ── The menubar ──────────────────────────────────────────────────────────────

/**
 * Props are App-scoped capabilities only. Everything the blueprint editor owns
 * arrives through the menubarCommands registry, so this component renders
 * identically whether or not an editor is mounted.
 */
export default function AppMenuBar({
  character = null,
  characters = [],
  recents = [],
  rigStatus = null,
  rigStatusById = null,
  loading = false,
  loadingStage = null,
  loadProgress = null,
  dirtyState = 'clean',
  onSwitchCharacter,
  switchingTo,
  onOpenLauncher,
  onOpenConversation,
  onOpenAppearance,
  onOpenShortcutHelp,
  onPopOut,
  onToggleTune, isTuneVisible = false,
  onToggleAnim, isAnimVisible = false,
  onToggleRigPreview, isRigPreviewVisible = false,
  viewLabel = 'PERSPECTIVE',
}) {
  const [open, setOpen] = useState(null)   // 'chip' | 'file' | 'character' | …
  const rootRef = useRef(null)
  const importGraphRef = useRef(null)
  const importCardRef = useRef(null)
  const importAllRef = useRef(null)

  // Editor-published commands + state. One subscription; the tick re-renders.
  const [registry, setRegistry] = useState(() => menubarCommands.get())
  useEffect(() => menubarCommands.subscribe(setRegistry), [])

  // The Assets ▸ "Assets dock" check mirrors the dock's open state (shellLayout).
  const [dockOpen, setDockOpen] = useState(() => shellLayout.getState().dockOpen)
  useEffect(() => shellLayout.subscribe(s => setDockOpen(s.dockOpen)), [])
  // View ▸ "Agent" mirrors the agent column the same way (ENG-1029).
  const [agentOpen, setAgentOpen] = useState(() => shellLayout.getState().agentOpen)
  useEffect(() => shellLayout.subscribe(s => setAgentOpen(s.agentOpen)), [])

  // Texture quality lives in the textureQuality module singleton (ENG-757) —
  // this tick only re-renders the labels when it changes.
  const [, setQualityTick] = useState(0)
  useEffect(() => subscribeTextureQuality(() => setQualityTick(t => t + 1)), [])

  const close = useCallback(() => setOpen(null), [])

  useEffect(() => {
    if (!open) return
    const onDown = e => { if (!rootRef.current?.contains(e.target)) setOpen(null) }
    const onKey = e => { if (e.key === 'Escape') setOpen(null) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // ── Availability ───────────────────────────────────────────────────────────
  const has = id => typeof registry.commands[id] === 'function'
  const run = (id, ...args) => { close(); menubarCommands.run(id, ...args) }
  /** The reason an editor-scoped command can't run, or null when it can. */
  const editorReason = id => (has(id) ? null : NO_EDITOR)
  /** The reason an app-scoped (shell) callback can't run, or null. */
  const appReason = fn => (typeof fn === 'function' ? null : NO_CHARACTER)
  const characterReason = character ? null : NO_CHARACTER

  const activeBP = registry.state.activeBP ?? null
  const tabs = Array.isArray(registry.state.tabs) ? registry.state.tabs : []
  const collabEnabled = !!registry.state.collabEnabled
  const collabSynced = !!registry.state.collabSynced

  const app = (fn) => () => { close(); fn?.() }

  // ── App-scoped handlers ────────────────────────────────────────────────────

  // "All characters' blueprints" — every aos-blueprint* key, the bundle the old
  // AppMenu wrote. Distinct file name from the editor's per-character export so
  // the two downloads can never be confused for each other.
  const exportAllBlueprints = () => {
    close()
    const { data, skipped } = collectBlueprintEntries(localStorage)
    if (skipped.length) console.warn('[AppMenuBar] skipped unparseable blueprint keys:', skipped)
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'blueprints.json'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  // Import ONE blueprint card from a file (ENG-1030). ADDITIVE: it creates a
  // new blueprint_asset row and never touches an existing graph, which is the
  // whole difference from the legacy import below. The new card is not made the
  // character's default — that stays an explicit decision on the card.
  const importBlueprintCard = async e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    close()
    const parsed = await readBlueprintImportFile(file)
    if (!parsed.ok) {
      await dialog.alert({
        title: 'Not a blueprint export',
        body: `“${file.name}” was not imported. ${parsed.reason}`,
        variant: 'error',
      })
      return
    }
    try {
      const { row, kind, nodeCount } = await importBlueprintEnvelope(parsed.envelope)
      await dialog.alert({
        title: 'Blueprint imported',
        body: `Created a new ${kind} card “${row.name}” with ${nodeCount} node${nodeCount === 1 ? '' : 's'}. `
          + 'Nothing existing was changed — open it from the content browser, and use “Set as default” '
          + 'when you want the character to load it.',
      })
    } catch (err) {
      await dialog.alert({
        title: 'Import failed',
        body: err?.message ?? String(err),
        variant: 'error',
      })
    }
  }

  const importAllBlueprints = e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const reader = new FileReader()
    reader.onload = async ev => {
      let parsed
      try {
        parsed = JSON.parse(ev.target.result)
      } catch {
        await dialog.alert({ title: 'Invalid blueprint file', body: 'That file is not valid JSON.', variant: 'error' })
        return
      }
      const { applied, ignored } = applyBlueprintEntries(parsed, localStorage)
      if (ignored.length) console.warn('[AppMenuBar] import ignored non-blueprint keys:', ignored)
      if (!applied.length) {
        await dialog.alert({
          title: 'Nothing imported',
          body: 'That file contained no aos-blueprint* entries, so nothing was changed.',
          variant: 'warning',
        })
        return
      }
    }
    reader.readAsText(file)
  }

  const loadPreset = async url => {
    close()
    const charLabel = character?.label ?? character?.id ?? 'this character'
    const ok = await dialog.confirm({
      title: 'Load shipped blueprint?',
      body: `This overwrites the local blueprint draft for “${charLabel}”. The server copy is not touched — Save to Cloud to publish.`,
      confirmLabel: 'Load',
    })
    if (!ok) return
    try {
      await loadBlueprint(url, { characterId: character?.id ?? null })
    } catch (e) {
      console.error('[AppMenuBar] load preset failed:', e)
      await dialog.alert({ title: 'Could not load preset', body: String(e?.message ?? e), variant: 'error' })
    }
  }

  // The ONE blueprint reset in the app. Clears every aos-blueprint* key and
  // reloads onto the shipped blueprint — see blueprintRecovery.js for why the
  // clear-and-broadcast variant the floating menu used did not survive.
  const resetBlueprints = async () => {
    close()
    const ok = await dialog.confirm({
      title: 'Reset ALL blueprints to shipped defaults?',
      body: 'Every local blueprint draft — body, face and events, for every character — is discarded and the bundled conversational blueprint is loaded. Anything not synced to the cloud is lost. Hold Reset to confirm.',
      confirmLabel: 'Reset',
      danger: true,
      holdToConfirm: { confirmId: 'menubar:reset-blueprints', testId: 'menubar-reset-blueprints-confirm' },
    })
    if (!ok) return
    const { nextUrl } = resetAllBlueprints(localStorage)
    window.location.href = nextUrl
  }

  const clearCache = async () => {
    close()
    const ok = await dialog.confirm({
      title: 'Clear ALL local cache?',
      body: 'Discards every local blueprint and authoring draft plus the cached animation, splat and decoder binaries, then reloads. Server data is not affected. Hold Clear to confirm.',
      confirmLabel: 'Clear',
      danger: true,
      holdToConfirm: { confirmId: 'menubar:clear-cache', testId: 'menubar-clear-cache-confirm' },
    })
    if (!ok) return
    clearAllLocalStorage(localStorage)
    // Also drop the persistent binary caches (animation GLBs + splats, the AAM
    // /ogs decoders + hair.gsh, and the ORT wasm + riglogic.wasm). Without this
    // a stale or corrupt cached asset survives a "clear" and keeps the avatar
    // broken. Best-effort — never block the reload on a cache error.
    try {
      await Promise.all([animCache.clearAll(), clearSplatCache(), clearAamFileCache(), clearStaticAssetCache()])
    } catch (e) {
      console.warn('[AppMenuBar] cache clearAll failed (continuing):', e)
    }
    window.location.reload()
  }

  // Build menu (Unreal-style). Reads the live environment directly, so it works
  // with or without an editor — and reports "no environment" as a DISABLED item
  // rather than an alert after the click.
  const envAssetId = useMemo(() => {
    // The id the server stated for the loaded environment, carried on the store.
    // Parsing it out of the splat URL reported "no environment" for every
    // org-layout environment (ENG-999).
    return activeEnvAssetId()
    // Recomputed whenever a menu opens, which is the only moment it's read.
  }, [open])

  const openBuild = which => {
    close()
    // Navmesh has no window any more: its params and Build button are the NAVMESH
    // row's own properties, so this points at that row instead of opening a
    // second place to set the same values (ENG-1050). And it does NOT require an
    // environment: that row is meaningful without one (the studio floor grid is
    // a navmesh), so the old bake-only guard would make the menu item dead on
    // exactly the scene people demo from.
    if (which === 'navmesh') {
      menubarCommands.run('outliner.select', 'navmesh')
      return
    }
    if (!envAssetId) return
    bakeSettingsStore.open(envAssetId)
  }

  const resetViewport = () => {
    close()
    window.__FRAME_AVATAR__?.()
  }

  // Hovering a sibling while a menu is open switches to it — standard menubar
  // behaviour, and it makes scanning the seven surfaces cheap.
  const hoverOpen = id => () => setOpen(o => (o && o !== id ? id : o))
  const toggle = id => () => setOpen(o => (o === id ? null : id))

  return (
    <div
      ref={rootRef}
      data-testid="app-menubar"
      // The app's top chrome, declared for LAYOUT rather than for tests:
      // windowStore.chromeTop measures this element to decide where a window
      // opens, because the bar is content-sized and grows with the UI scale.
      // A test id is a test's handle and may be renamed with a spec; this is a
      // contract with the window system, so it says so in its own attribute.
      data-app-chrome="menubar"
      style={{
        position: 'relative', zIndex: 6000, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 2,
        padding: '6px 10px',
        background: 'var(--m-panel)',
        borderBottom: '1px solid var(--m-line)',
      }}
    >
      <CharacterChip
        character={character}
        switchingTo={switchingTo}
        characters={characters}
        recents={recents}
        rigStatus={rigStatus}
        rigStatusById={rigStatusById}
        loading={loading}
        loadingStage={loadingStage}
        progress={loadProgress}
        dirtyState={dirtyState}
        open={open === 'chip'}
        onToggle={toggle('chip')}
        onSwitch={id => { close(); onSwitchCharacter?.(id) }}
        onOpenLauncher={() => { close(); onOpenLauncher?.() }}
        switchReason={typeof onSwitchCharacter === 'function'
          ? null
          : 'no characters to switch between yet'}
      />

      <span style={{ width: 1, height: 22, background: 'var(--m-line)', margin: '0 8px' }} />

      {/* ── File ── */}
      <div style={{ position: 'relative' }}>
        <MenuButton testId="menubar-menu-file" label="File" open={open === 'file'} onClick={toggle('file')} onMouseEnter={hoverOpen('file')} />
        <Dropdown open={open === 'file'} testId="menubar-dropdown-file" minWidth={330}>
          <SectionLabel>Blueprint cards</SectionLabel>
          {/* A blueprint is one CARD per graph now (ENG-1030), so export lives
              on the card: right-click it in the content browser, or use its
              inspector. Import is here because there is no card to click yet —
              it CREATES one, additively, and never overwrites an existing
              graph. Dropping the file on the content browser does the same. */}
          <Item
            testId="menubar-item-import-blueprint-card"
            label="↑ Import blueprint from file…"
            note=".aosbp.json → a new card"
            onClick={() => { close(); importCardRef.current?.click() }}
          />
          <Divider />
          <SectionLabel>Legacy whole-blueprint file</SectionLabel>
          {/* Relabelled rather than removed. These two predate blueprints being
              asset rows: they compose and overwrite the localStorage editor
              caches for all three graphs at once, which is the wholesale
              overwrite the card path exists to avoid. Kept because they are
              still the only way to move all three graphs in one file — removing
              a working escape hatch in the same change that adds its
              replacement strands anyone mid-migration. */}
          <Item
            testId="menubar-item-export-graph"
            label="↓ Export all three graphs…"
            note="blueprint.json · legacy format"
            disabledReason={editorReason('file.exportGraph')}
            onClick={() => run('file.exportGraph')}
          />
          <Item
            testId="menubar-item-import-graph"
            label="↑ Import all three graphs…"
            note="overwrites body + face + events"
            disabledReason={editorReason('file.importGraph')}
            onClick={() => { close(); importGraphRef.current?.click() }}
          />
          <Divider />
          <SectionLabel>Every character</SectionLabel>
          {/* Distinct labels AND distinct file names. These two exports used to
              both be called "Export". */}
          <Item
            testId="menubar-item-export-all"
            label="↓ Export all characters’ blueprints…"
            note="blueprints.json"
            onClick={exportAllBlueprints}
          />
          <Item
            testId="menubar-item-import-all"
            label="↑ Import all characters’ blueprints…"
            onClick={() => { close(); importAllRef.current?.click() }}
          />
          <Divider />
          <SectionLabel>Cloud</SectionLabel>
          {collabEnabled ? (
            <>
              <Item
                testId="menubar-item-flush-collab"
                label="🔄 Flush now"
                disabledReason={editorReason('file.flushCollab')}
                onClick={() => run('file.flushCollab')}
              />
              <div style={{
                padding: '4px 11px 6px 30px', fontFamily: UI_FONT, fontSize: 9.5, letterSpacing: '.05em',
                color: collabSynced ? 'var(--m-online)' : 'var(--m-warn)',
              }}>{collabSynced ? '● Live' : '◯ Connecting…'}</div>
            </>
          ) : (
            <>
              <Item
                testId="menubar-item-save-cloud"
                label="⬆ Save to Cloud"
                disabledReason={editorReason('file.saveToCloud')}
                onClick={() => run('file.saveToCloud')}
              />
              <Item
                testId="menubar-item-load-cloud"
                label="⬇ Load from Cloud"
                disabledReason={editorReason('file.loadFromCloud')}
                onClick={() => run('file.loadFromCloud')}
              />
            </>
          )}
          <Divider />
          <SectionLabel>Load shipped blueprint</SectionLabel>
          {PRESETS.map(p => (
            <Item
              key={p.url}
              testId={`menubar-item-preset-${p.url}`}
              label={`⌂ ${p.label}`}
              onClick={() => loadPreset(p.url)}
            />
          ))}
          <Divider />
          <SectionLabel>Recovery</SectionLabel>
          {/* Both destructive items go through a confirm dialog whose confirm
              button is a sustained hold (HoldToConfirm) — same gesture the
              launcher's delete uses, so "irreversible" has one shape app-wide. */}
          <Item
            testId="menubar-item-reset-graph"
            label="↺ Reset this graph"
            disabledReason={editorReason('file.resetGraph') ?? (registry.state.canResetGraph === false ? 'only the body and face graphs' : null)}
            onClick={() => run('file.resetGraph')}
          />
          <Item
            testId="menubar-item-reset-blueprints"
            label="↻ Reset all blueprints to shipped defaults…"
            danger
            onClick={resetBlueprints}
          />
          <Item
            testId="menubar-item-clear-cache"
            label="⌫ Clear local cache…"
            danger
            onClick={clearCache}
          />
        </Dropdown>
        <input
          ref={importCardRef} type="file" accept=".json" style={{ display: 'none' }}
          onChange={importBlueprintCard}
        />
        <input
          ref={importGraphRef} type="file" accept=".json" style={{ display: 'none' }}
          onChange={e => menubarCommands.run('file.importGraph', e)}
        />
        <input
          ref={importAllRef} type="file" accept=".json" style={{ display: 'none' }}
          onChange={importAllBlueprints}
        />
      </div>

      {/* ── Character ── */}
      <div style={{ position: 'relative' }}>
        <MenuButton testId="menubar-menu-character" label="Character" open={open === 'character'} onClick={toggle('character')} onMouseEnter={hoverOpen('character')} />
        <Dropdown open={open === 'character'} testId="menubar-dropdown-character">
          {/* Switching lives on the chip now — the old Character menu listed every
              character AND so did the floating menu. One switcher, one place. */}
          <Item
            testId="menubar-item-conversation"
            label="◆ Conversation…"
            disabledReason={appReason(onOpenConversation) ?? characterReason}
            onClick={app(onOpenConversation)}
          />
          {/* Full-viewport blueprint editors (build spec: no mode toggle in the
              chrome — these ARE the way into the graphs). */}
          <Item
            testId="menubar-item-edit-body"
            label={`${SURFACE_GLYPH.body} Body Blueprint…`}
            checked={activeBP === 'body'}
            disabledReason={editorReason('editors.open')}
            onClick={() => run('editors.open', 'body')}
          />
          <Item
            testId="menubar-item-edit-face"
            label={`${SURFACE_GLYPH.face} Face Blueprint…`}
            checked={activeBP === 'face'}
            disabledReason={editorReason('editors.open')}
            onClick={() => run('editors.open', 'face')}
          />
          <Item
            testId="menubar-item-prefab-editor"
            label="✂ Prefab Editor…"
            checked={activeBP === 'prefab'}
            disabledReason={editorReason('editors.open')}
            onClick={() => run('editors.open', 'prefab')}
          />
          <Divider />
          {/* ENG-1028. Published by App, not the blueprint editor — ownership and
              visibility belong to the character, so the item works with no editor
              surface open. The reason is NO_CHARACTER rather than editorReason's
              "blueprint editor not open", which would send someone to open a
              graph to fix a missing character. */}
          <Item
            testId="menubar-item-owner-visibility"
            label="⚿ Owner & visibility…"
            disabledReason={has('character.ownerVisibility') ? null : NO_CHARACTER}
            onClick={() => run('character.ownerVisibility')}
          />
          {/* ENG-1079: snapshot the live viewport as this character's launcher
              card image. Published by App only when a character is loaded. */}
          <Item
            testId="menubar-item-capture-thumbnail"
            label="◑ Capture launcher thumbnail"
            disabledReason={has('character.captureThumbnail') ? null : NO_CHARACTER}
            onClick={() => run('character.captureThumbnail')}
          />
          <Item
            testId="menubar-item-create-character"
            label="+ Create new character…"
            disabledReason={editorReason('character.createNew')}
            onClick={() => run('character.createNew')}
          />
        </Dropdown>
      </div>

      {/* ── Animation ── */}
      <div style={{ position: 'relative' }}>
        <MenuButton testId="menubar-menu-animation" label="Animation" open={open === 'animation'} onClick={toggle('animation')} onMouseEnter={hoverOpen('animation')} />
        <Dropdown open={open === 'animation'} testId="menubar-dropdown-animation">
          <Item
            testId="menubar-item-animator"
            label="🎬 Animator"
            checked={activeBP === 'animator'}
            disabledReason={editorReason('editors.open')}
            onClick={() => run('editors.open', 'animator')}
          />
          <Item
            testId="menubar-item-edit-events"
            label={`${SURFACE_GLYPH.events} Event Graph…`}
            checked={activeBP === 'events'}
            disabledReason={editorReason('editors.open')}
            onClick={() => run('editors.open', 'events')}
          />
        </Dropdown>
      </div>

      {/* ── Assets ── */}
      <div style={{ position: 'relative' }}>
        <MenuButton testId="menubar-menu-assets" label="Assets" open={open === 'assets'} onClick={toggle('assets')} onMouseEnter={hoverOpen('assets')} />
        <Dropdown open={open === 'assets'} testId="menubar-dropdown-assets">
          {/* The content browser is the bottom DOCK, not an editor surface. */}
          <Item
            testId="menubar-item-content-browser"
            label="📁 Assets dock"
            checked={dockOpen}
            hint={formatKeys(getShortcut('toggle-assets-dock'))}
            onClick={() => { close(); shellLayout.toggleDock() }}
          />
          <Item
            testId="menubar-item-audio-clips"
            label="🔊 Audio Clips"
            checked={activeBP === 'audio-assets'}
            disabledReason={editorReason('editors.open')}
            onClick={() => run('editors.open', 'audio-assets')}
          />
        </Dropdown>
      </div>

      {/* ── Build ── */}
      <div style={{ position: 'relative' }}>
        <MenuButton testId="menubar-menu-build" label="Build" open={open === 'build'} onClick={toggle('build')} onMouseEnter={hoverOpen('build')} />
        <Dropdown open={open === 'build'} testId="menubar-dropdown-build">
          <Item
            testId="menubar-item-build-lighting"
            label="💡 Build Lighting"
            disabledReason={envAssetId ? null : NO_ENVIRONMENT}
            onClick={() => openBuild('lighting')}
          />
          {/* Enabled with or without an environment: it now opens the navmesh
              row's properties, and that row has an answer either way (a baked
              navmesh, or the studio floor grid). The row's own Build button is
              what carries the needs-an-environment reason. */}
          <Item
            testId="menubar-item-build-navmesh"
            label="🧭 Build Navmesh"
            onClick={() => openBuild('navmesh')}
          />
        </Dropdown>
      </div>

      {/* ── View ── */}
      <div style={{ position: 'relative' }}>
        <MenuButton testId="menubar-menu-view" label="View" open={open === 'view'} onClick={toggle('view')} onMouseEnter={hoverOpen('view')} />
        <Dropdown open={open === 'view'} testId="menubar-dropdown-view" minWidth={272}>
          {/* Live Mode is gone (Wave 2B "one mode"): the app is always
              playing — there is no Standard/Live fork left to toggle. */}
          <Item
            testId="menubar-item-tune-panel"
            label="🎛 Tune Panel"
            checked={isTuneVisible}
            disabledReason={appReason(onToggleTune)}
            onClick={app(onToggleTune)}
          />
          <Item
            testId="menubar-item-anim-panel"
            label="🎮 Anim Test Panel"
            checked={isAnimVisible}
            disabledReason={appReason(onToggleAnim)}
            onClick={app(onToggleAnim)}
          />
          <Item
            testId="menubar-item-rig-preview"
            label="🎚️ Rig Preview"
            checked={isRigPreviewVisible}
            disabledReason={appReason(onToggleRigPreview)}
            onClick={app(onToggleRigPreview)}
          />
          <Item
            testId="menubar-item-agent-panel"
            label="🤖 Agent"
            hint={formatKeys(getShortcut('toggle-agent'))}
            checked={agentOpen}
            onClick={() => { close(); shellLayout.toggleAgent() }}
          />
          <Item
            testId="menubar-item-reset-viewport"
            label="⌖ Reset Viewport"
            hint={formatKeys(getShortcut('frame-avatar'))}
            disabledReason={characterReason}
            onClick={resetViewport}
          />
          <Item
            testId="menubar-item-shortcut-help"
            label="⌨ Keyboard Shortcuts…"
            hint={formatKeys(getShortcut('shortcut-help'))}
            disabledReason={appReason(onOpenShortcutHelp)}
            onClick={app(onOpenShortcutHelp)}
          />
          <Divider />
          <Item
            testId="menubar-item-settings"
            label="⚙ Settings…"
            note="colors · display · scene · account"
            disabledReason={appReason(onOpenAppearance)}
            onClick={app(onOpenAppearance)}
          />
          <Divider />
          <SectionLabel>Texture quality</SectionLabel>
          {/* YouTube-style: Auto (showing what it resolved to) + manual
              overrides. Selecting re-targets the loaded textures live via the
              textureQuality subscription in AvatarModel — no reload. */}
          {['auto', ...TIER_ORDER].map(t => (
            <Item
              key={t}
              testId={`menubar-item-texq-${t}`}
              label={t === 'auto' ? `Auto (${getAutoTier()})` : t.charAt(0).toUpperCase() + t.slice(1)}
              checked={getSetting() === t}
              onClick={() => { close(); setSetting(t) }}
            />
          ))}
          <Divider />
          <Item
            testId="menubar-item-pop-out"
            label="⧉ Pop out blueprint editor"
            disabledReason={appReason(onPopOut)}
            onClick={app(onPopOut)}
          />
        </Dropdown>
      </div>

      <span style={{ flex: 1 }} />

      {/* The centred ▶/■ play transport is gone (Wave 2B "one mode"): the
          app is always playing, so there is no Standard/Live toggle for a
          transport to drive. */}

      {/* ── Right side: the view label (build spec) ──
          NO tab strip here — the docked shell has no mode toggle in its chrome.
          Body / Face / Event Graph open as full-viewport editors from the
          Character and Animation menus. The old CONTENT ▾ toggle is gone
          (Wave 2E): it duplicated the assets dock's three other affordances —
          Assets ▸ "Assets dock", ⌥A, and the collapsed dock strip. */}
      <span
        data-testid="menubar-view-label"
        style={{ fontFamily: UI_FONT, fontSize: 9, color: 'var(--m-fg-3)', letterSpacing: '.10em', marginRight: 8, whiteSpace: 'nowrap' }}
      >{viewLabel}</span>
    </div>
  )
}
